/**
 * AdminDataRequests — admin roster of pending GDPR data requests.
 * Hits /api/admin/data-requests.
 */
import { useEffect, useState } from "react";
import { ClipboardList } from "lucide-react";
import { api } from "../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function AdminDataRequests({ testid = "admin-data-requests" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/data-requests")
      .then((r) => alive && setRows(r.data || []))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      data-testid={testid}
      className="rounded-3xl bg-white border hairline p-6"
    >
      <div className="flex items-baseline gap-3">
        <ClipboardList size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
        <span className="eyebrow">Pending data requests</span>
      </div>
      <p className="mt-2 text-xs text-[#525252]">
        Export + delete requests from your families. Both wait calmly through
        the cooling-off window.
      </p>
      {loading ? (
        <p
          data-testid={`${testid}-loading`}
          className="mt-5 font-serif italic text-[#525252] text-sm"
        >
          A quiet moment…
        </p>
      ) : rows.length === 0 ? (
        <p
          data-testid={`${testid}-empty`}
          className="mt-5 font-serif italic text-[#525252] text-sm"
        >
          Nothing pending. All quiet.
        </p>
      ) : (
        <ul
          data-testid={`${testid}-list`}
          className="mt-5 divide-y divide-[#171717]/6"
        >
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`${testid}-row-${r.id}`}
              className="py-3 flex items-start gap-3"
            >
              <span
                aria-hidden
                className="mt-1.5 h-2 w-2 rounded-full"
                style={{
                  background: r.kind === "delete" ? "#A89CC8" : "#8A9984",
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#171717]">
                  {r.user_name || r.user_email}
                </p>
                <p className="text-[11px] tracking-[0.18em] uppercase text-[#525252] mt-0.5">
                  {r.kind === "delete" ? "Removal" : "Export"} · {r.status}
                </p>
                {r.cooling_off_until && (
                  <p className="text-[11px] text-[#525252] mt-1">
                    Cooling-off ends · {fmt(r.cooling_off_until)}
                  </p>
                )}
              </div>
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252] shrink-0">
                {fmt(r.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
